import { type CSSProperties, type FC, useCallback, useEffect, useRef } from 'react';

import cn from 'classnames';

import { PageLoaderProps } from './types';

import styles from './styles.module.scss';

/**
 * Визуализация загрузки страницы.
 * Подгоняет высоту под доступную область экрана и сохраняет фон.
 */
export const PageLoader: FC<PageLoaderProps> = ({ backgroundColor, className }) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);

	const setLoaderHeight = useCallback(() => {
		const loaderEl = containerRef.current;
		const contentEl = contentRef.current;
		if (!loaderEl || !contentEl) return;

		const contentHeight = contentEl.getBoundingClientRect().height;
		const bodyHeight = document.documentElement.clientHeight;
		const loaderTop = loaderEl.getBoundingClientRect().top;
		const availableHeight = bodyHeight - loaderTop;
		const loaderHeight = availableHeight < contentHeight ? contentHeight : availableHeight;

		loaderEl.style.height = `${loaderHeight}px`;
	}, []);

	useEffect(() => {
		setLoaderHeight();
		window.addEventListener('resize', setLoaderHeight);

		return () => window.removeEventListener('resize', setLoaderHeight);
	}, [setLoaderHeight]);

	const style: CSSProperties | undefined = backgroundColor ? { backgroundColor } : undefined;

	return (
		<div
			ref={containerRef}
			aria-live="polite"
			className={cn(styles.Loader, className)}
			role="status"
			style={style}
		>
			<div ref={contentRef} className={styles.Content}>
				<div className={styles.LoaderIcon} />
			</div>
		</div>
	);
};
